interface LogoProps {
  size?: 'sm' | 'md' | 'lg'
  showText?: boolean
  className?: string
}

export function Logo({ size = 'md', showText = true, className = '' }: LogoProps) {
  const dimensions = {
    sm: { width: 350, height: 80 },
    md: { width: 525, height: 150 },
    lg: { width: 700, height: 200 }
  }

  const textSizes = {
    sm: { main: 26, sub: 20 },
    md: { main: 32, sub: 24 },
    lg: { main: 42, sub: 32 }
  }

  const textPositions = {
    sm: { mainY: 55, subX: 320, subY: 55 },
    md: { mainY: 80, subX: 470, subY: 80 },
    lg: { mainY: 105, subX: 620, subY: 105 }
  }

  const decorativePositions = {
    sm: { x: 360, y: 40 },
    md: { x: 510, y: 55 },
    lg: { x: 680, y: 70 }
  }

  const { width, height } = dimensions[size]
  const { main: mainTextSize, sub: subTextSize } = textSizes[size]
  const { mainY, subX, subY } = textPositions[size]
  const { x: decorativeX, y: decorativeY } = decorativePositions[size]

  return (
    <div className={`inline-flex items-center ${className}`}>
      <svg 
        width={width} 
        height={height} 
        viewBox="0 0 525 150" 
        xmlns="http://www.w3.org/2000/svg"
        className="drop-shadow-sm"
      >
        <defs>
          <linearGradient id="swirlGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{stopColor:"#3B82F6", stopOpacity:1}} />
            <stop offset="50%" style={{stopColor:"#8B5CF6", stopOpacity:1}} />
            <stop offset="100%" style={{stopColor:"#06B6D4", stopOpacity:1}} />
          </linearGradient>
          <linearGradient id="textGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{stopColor:"#1F2937", stopOpacity:1}} />
            <stop offset="100%" style={{stopColor:"#374151", stopOpacity:1}} />
          </linearGradient>
        </defs>
        
        <g transform="translate(30,30) scale(1.4)">
          <path 
            d="M40,10 C55,10 65,20 65,35 C65,50 55,60 40,60 C25,60 15,50 15,35 C15,25 20,20 30,20 C35,20 40,25 40,30 C40,35 35,40 30,40 C27,40 25,38 25,35" 
            fill="none" 
            stroke="url(#swirlGradient)" 
            strokeWidth="4" 
            strokeLinecap="round"
          />
          
          <path 
            d="M45,15 C50,15 55,20 55,25 C55,30 50,35 45,35" 
            fill="none" 
            stroke="url(#swirlGradient)" 
            strokeWidth="3" 
            strokeLinecap="round" 
            opacity="0.7"
          />
          
          <circle cx="32" cy="28" r="2" fill="url(#swirlGradient)"/>
          <circle cx="48" cy="42" r="1.5" fill="url(#swirlGradient)" opacity="0.8"/>
          <circle cx="38" cy="48" r="1" fill="url(#swirlGradient)" opacity="0.6"/>
        </g>
        
        {showText && (
          <>
            <text 
              x="140" 
              y={mainY} 
              fontFamily="Arial, sans-serif" 
              fontSize={mainTextSize} 
              fontWeight="bold" 
              fill="url(#textGradient)"
            >
              ContractorPro
            </text>
            
            <text 
              x={subX} 
              y={subY} 
              fontFamily="Arial, sans-serif" 
              fontSize={subTextSize} 
              fontWeight="normal" 
              fill="url(#swirlGradient)"
            >
              .ai
            </text>
          </>
        )}
        
        <g transform={`translate(${decorativeX},${decorativeY})`} opacity="0.3">
          <rect x="0" y="0" width="15" height="2" fill="#3B82F6" rx="1"/>
          <rect x="0" y="4" width="12" height="2" fill="#8B5CF6" rx="1"/>
          <rect x="0" y="8" width="18" height="2" fill="#06B6D4" rx="1"/>
        </g>
      </svg>
    </div>
  )
}

export function LogoIcon({ size = 32, className = '' }: { size?: number, className?: string }) {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 80 80" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        <linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{stopColor:"#3B82F6", stopOpacity:1}} />
          <stop offset="50%" style={{stopColor:"#8B5CF6", stopOpacity:1}} />
          <stop offset="100%" style={{stopColor:"#06B6D4", stopOpacity:1}} />
        </linearGradient>
      </defs>
      
      <g transform="translate(8,8)">
        <path 
          d="M32,8 C40,8 48,16 48,28 C48,40 40,48 32,48 C20,48 12,40 12,28 C12,20 16,16 24,16 C28,16 32,20 32,24 C32,28 28,32 24,32 C22,32 20,30 20,28" 
          fill="none" 
          stroke="url(#iconGradient)" 
          strokeWidth="3" 
          strokeLinecap="round"
        />
        
        <path 
          d="M36,12 C40,12 44,16 44,20 C44,24 40,28 36,28" 
          fill="none" 
          stroke="url(#iconGradient)" 
          strokeWidth="2.4" 
          strokeLinecap="round" 
          opacity="0.7"
        />
        
        <circle cx="25.6" cy="22.4" r="1.6" fill="url(#iconGradient)"/>
        <circle cx="38.4" cy="33.6" r="1.2" fill="url(#iconGradient)" opacity="0.8"/>
        <circle cx="30.4" cy="38.4" r="0.8" fill="url(#iconGradient)" opacity="0.6"/>
      </g>
    </svg>
  )
}